import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useFmtPrice } from '../org/OrgContext.jsx'

// Standalone page (NOT a modal) reached from the prepay offer notification —
// /prepay/2026-08-01 — and from the banner on "My enrollments".
//
// It shows one row per (participant, course) that can still be bought in
// advance for the month, with the maths already done by the server: gross
// (price x SCHEDULED sessions), the coupon that applied, the prepay reduction
// and what is left to pay. Every row is TICKED by default; the user can untick
// any of them and the total follows.
//
// Flat month cover: the price is the full scheduled month. Missing a session
// does not reduce it, there is no rollover and no refund — that is the deal,
// and the note under the table says so.
//
// The offer is always recomputed on the server, both here and again at
// checkout, so a stale link cannot create a payment: lines already invoiced or
// already prepaid simply are not in the list.
//
// There is a DEADLINE (organization.prepay_deadline_day, default the 10th).
// After it the server answers `open: false` and refuses the checkout with
// `prepay_closed`. The table is still shown then — greyed out, with the
// deadline spelled out — because "here is what you missed, and when the door
// closed" reads better than an empty page; only the pay button goes away.

export function PrepayPage() {
  const { t, i18n } = useTranslation()
  const { month: monthParam } = useParams()
  const { user, loading: authLoading } = useAuth()
  const fmtPrice = useFmtPrice()

  const [data, setData] = useState(null)
  const [picked, setPicked] = useState(() => new Set())
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const keyOf = (l) => `${l.participant_id}:${l.course_id}`

  function reload() {
    setLoading(true); setError(null)
    api.prepay.offer(monthParam)
      .then((d) => {
        setData(d)
        // All ticked by default — the offer is meant to be taken whole.
        setPicked(new Set((d.lines || []).map(keyOf)))
      })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !user) return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, monthParam])

  const lines = data?.lines || []

  // `open` is only false once the server has answered; while loading we do not
  // want the closed notice to flash, hence the explicit === false.
  // Declared BEFORE the totals memo on purpose — the memo reads it, and a
  // `const` used above its declaration is a ReferenceError, not a warning.
  const closed = data ? data.open === false : false

  // Totals follow the ticks — the server recomputes them anyway, this is only
  // so the number under the button matches what is checked.
  const totals = useMemo(() => {
    let gross = 0, discount = 0, saving = 0, amount = 0
    for (const l of lines) {
      // After the deadline nothing is tickable, so the total is simply what
      // the whole month WOULD have cost — the point of still showing it.
      if (!closed && !picked.has(keyOf(l))) continue
      gross    += Number(l.gross || 0)
      discount += Number(l.discount_amount || 0)
      saving   += Number(l.prepay_amount || 0)
      amount   += Number(l.amount || 0)
    }
    return { gross, discount, saving, amount }
  }, [lines, picked, closed])

  const deadlineLabel = useMemo(() => {
    if (!data?.deadline_date) return ''
    try {
      const d = new Date(`${data.deadline_date}T00:00:00`)
      return new Intl.DateTimeFormat(i18n.language || 'ro',
        { day: 'numeric', month: 'long', year: 'numeric' }).format(d)
    } catch {
      return data.deadline_date
    }
  }, [data, i18n.language])

  const monthLabel = useMemo(() => {
    if (!data?.month) return ''
    try {
      const d = new Date(`${data.month}T00:00:00`)
      const s = new Intl.DateTimeFormat(i18n.language || 'ro', { month: 'long', year: 'numeric' }).format(d)
      return s.charAt(0).toUpperCase() + s.slice(1)
    } catch {
      return data.month_label || data.month
    }
  }, [data, i18n.language])

  function toggle(l) {
    const k = keyOf(l)
    setPicked((cur) => {
      const next = new Set(cur)
      if (next.has(k)) next.delete(k); else next.add(k)
      return next
    })
  }

  function toggleAll() {
    setPicked((cur) => (cur.size === lines.length ? new Set() : new Set(lines.map(keyOf))))
  }

  async function pay() {
    const chosen = lines.filter((l) => picked.has(keyOf(l)))
    if (chosen.length === 0) return
    setBusy(true); setError(null)
    try {
      const d = await api.prepay.checkout(
        data.month,
        chosen.map((l) => ({ participant_id: l.participant_id, course_id: l.course_id })),
      )
      if (d.url) window.location.assign(d.url)
      else { setError('checkout_failed'); setBusy(false) }
    } catch (e) {
      setError(e.code || 'server')
      setBusy(false)
      // A line that went stale between load and click — or a page left open
      // across the deadline: show the fresh truth.
      if (e.code === 'line_not_available' || e.code === 'prepay_closed') reload()
    }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user) {
    return (
      <main className="container">
        <h1>{t('prepay.title')}</h1>
        <p className="error">{t('auth.errors.not_authenticated')}</p>
        <p><Link to="/login">{t('nav.login')}</Link></p>
      </main>
    )
  }

  const allPicked = lines.length > 0 && picked.size === lines.length

  return (
    <main className="container">
      <p><Link to="/me/enrollments">&larr; {t('enrollments.title')}</Link></p>
      <h1>{t('prepay.title')}</h1>

      {data && (
        <p className="muted">
          {t('prepay.intro', { month: monthLabel, percent: data.percent })}
        </p>
      )}

      {/* The deadline gets its own line rather than trailing the intro in
          grey: it is the one fact on this page that expires, and a user who
          skims the muted paragraph is exactly the user who misses it. */}
      {data?.enabled && !closed && deadlineLabel && (
        <p className="notice">
          {t('prepay.deadline_note', { date: deadlineLabel })}{' '}
          {t('prepay.deadline_after')}
        </p>
      )}

      {!loading && data?.enabled && closed && (
        <p className="error">{t('prepay.closed', { date: deadlineLabel, month: monthLabel })}</p>
      )}

      {error && (
        <p className="error">
          {t(`prepay.errors.${error}`, {
            defaultValue: t(`auth.errors.${error}`, { defaultValue: error }),
          })}
        </p>
      )}

      {loading && <p>{t('common.loading')}</p>}

      {!loading && data && !data.enabled && (
        <p className="muted">{t('prepay.disabled')}</p>
      )}

      {!loading && data?.enabled && lines.length === 0 && (
        <p className="muted">
          {t('prepay.empty')}{' '}
          <Link to="/me/enrollments">{t('prepay.back_to_enrollments')}</Link>
        </p>
      )}

      {!loading && lines.length > 0 && (
        <section className="card">
          <table className="results-table">
            <thead>
              <tr>
                {!closed && (
                  <th>
                    <label className="checkbox">
                      <input type="checkbox" checked={allPicked} onChange={toggleAll}
                             aria-label={t('prepay.select_all')} />
                    </label>
                  </th>
                )}
                <th>{t('prepay.col_participant')}</th>
                <th>{t('prepay.col_course')}</th>
                <th>{t('prepay.col_sessions')}</th>
                <th>{t('prepay.col_gross')}</th>
                <th>{t('prepay.col_discount')}</th>
                <th>{t('prepay.col_saving')}</th>
                <th>{t('prepay.col_amount')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const on = !closed && picked.has(keyOf(l))
                return (
                  <tr key={keyOf(l)} className={on ? undefined : 'muted'}>
                    {!closed && (
                      <td>
                        <label className="checkbox">
                          <input type="checkbox" checked={on} onChange={() => toggle(l)}
                                 aria-label={`${l.participant_name} — ${l.course_name}`} />
                        </label>
                      </td>
                    )}
                    <td>{l.participant_name}</td>
                    <td>{l.course_name}</td>
                    <td>{t('prepay.n_sessions', { count: Number(l.sessions) })}</td>
                    <td>{fmtPrice(l.gross)}</td>
                    <td>
                      {Number(l.discount_amount) > 0
                        ? <>&minus;{fmtPrice(l.discount_amount)}{l.discount_label
                            ? <span className="muted small"> ({l.discount_label})</span> : null}</>
                        : <span className="muted">&mdash;</span>}
                    </td>
                    <td>&minus;{fmtPrice(l.prepay_amount)}</td>
                    <td><strong>{fmtPrice(l.amount)}</strong></td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <p className="muted small">{t('prepay.flat_month_note')}</p>

          <p>
            <strong>{t('prepay.total', { amount: fmtPrice(totals.amount) })}</strong>
            {totals.saving > 0 && (
              <>
                {' '}
                <span className="muted">
                  ({t('prepay.saving_total', { amount: fmtPrice(totals.saving) })})
                </span>
              </>
            )}
          </p>

          {closed ? (
            <p className="muted small">{t('prepay.closed_hint')}</p>
          ) : (
            <>
              <div className="form-actions">
                <button type="button" disabled={busy || picked.size === 0} onClick={pay}>
                  {busy ? t('common.loading') : t('prepay.pay', { amount: fmtPrice(totals.amount) })}
                </button>
                <button type="button" className="link-button" disabled={busy} onClick={reload}
                        style={{ marginLeft: '0.75rem' }}>
                  {t('prepay.refresh')}
                </button>
              </div>
              {/* Repeated at the decision point — the notice at the top has
                  scrolled away by the time anyone reaches this button. */}
              {deadlineLabel && (
                <p className="muted small" style={{ marginTop: '0.6rem' }}>
                  {t('prepay.deadline_at_pay', { date: deadlineLabel })}
                </p>
              )}
            </>
          )}
        </section>
      )}
    </main>
  )
}
